// Job Description Content for Modal (Facility view)
// Adapted from JobDescription.js with callback-based navigation

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import QRCode from 'react-native-qrcode-svg';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import WebModal from './WebModal';
import {
  getJob,
  getJobApplications,
  getJobQR,
  approveJob,
  reapproveJob,
  deleteJob,
  submitCancellation,
  rejectCancellation,
  acceptCancellation,
  getCancellationList,
} from '../../services/job';
import { webColors, webBorderRadius, webSpacing } from '../../constants/webStyles';
import { normalize } from '../../utils/normalize';
import { formatPrice, getJobDisplayCurrency } from '../../utils/currency';
import { jobTypes, isFullTimeJob } from '../../constants/lov/professions';
import { useIsMobile } from '../../hooks/useIsMobile';
import JobShareButton from '../../screens/Jobs/components/JobShareButton';

// Applicant Card Component (Custom for modal)
const ApplicantCard = ({ applicant, status, isApproved, onPress, onStatusChange }) => {
  const doctor = applicant.doctor || {};
  const name = doctor.first_name
    ? `${doctor.first_name} ${doctor.last_name?.[0] || ''}.`
    : applicant.name || 'Applicant';

  const statusStyles = {
    approved: { icon: '3', text: 'Approved', color: '#22c55e', bgColor: '#dcfce7' },
    applied: { icon: '0', text: 'Applied', color: '#22c55e', bgColor: '#dcfce7' },
    pending: { icon: '0', text: 'Pending', color: '#f59e0b', bgColor: '#fef3c7' },
    selected: { icon: '1', text: 'Pending Applicant', color: '#3b82f6', bgColor: '#dbeafe' },
    declined: { icon: '-1', text: 'Declined', color: '#ef4444', bgColor: '#fee2e2' },
    cancelled: { icon: '-4', text: 'Cancelled', color: '#6b7280', bgColor: '#f3f4f6' },
  };
  const s = statusStyles[status] || statusStyles.pending;

  return (
    <TouchableOpacity style={styles.applicantCard} onPress={onPress} activeOpacity={0.7}>
      {/* Avatar */}
      <View style={styles.applicantAvatar}>
        {doctor.profile_image ? (
          <Text style={styles.applicantAvatarText}>
            {name.charAt(0).toUpperCase()}
          </Text>
        ) : (
          <Text style={styles.applicantAvatarText}>
            {name.charAt(0).toUpperCase()}
          </Text>
        )}
      </View>

      {/* Info */}
      <View style={styles.applicantInfo}>
        <Text style={styles.applicantLocation}>{doctor.city || 'Location'}</Text>
        <Text style={styles.applicantName}>{name}</Text>
        <Text style={styles.applicantRole} numberOfLines={1}>
          {doctor.profession?.malaysia_name || doctor.about || 'Healthcare Professional'}
        </Text>
      </View>

      {/* Status Badge */}
      <View style={[styles.applicantStatusBadge, { backgroundColor: s.bgColor, borderColor: s.color }]}>
        <Text style={[styles.applicantStatusText, { color: s.color }]}>{s.text}</Text>
      </View>
    </TouchableOpacity>
  );
};

// Main Component
const JobDescriptionContent = ({
  job: initialJob,
  onViewProfile,
  onViewDetails,
  onEdit,
  onClose,
  onRefresh,
}) => {
  const queryClient = useQueryClient();
  const { isDoctor, currencyPreset } = useSelector((state) => state.user);

  const [refreshing, setRefreshing] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedApplicant, setSelectedApplicant] = useState(null);

  const isMobile = useIsMobile();

  // Fetch job details
  const {
    data: jobQuery,
    refetch: jobRefetch,
    isLoading: jobLoading,
  } = useQuery({
    queryKey: ['jobModal', initialJob?.uuid],
    queryFn: () => getJob(initialJob?.uuid),
    enabled: !!initialJob?.uuid,
  });
  const jobData = jobQuery?.data?.data || initialJob;

  // Fetch job applications
  const {
    data: jobApplicationsQuery,
    refetch: jobApplicationsRefetch,
    isLoading: jobApplicationsLoading,
  } = useQuery({
    queryKey: ['jobApplicationsModal', initialJob?.uuid],
    queryFn: () => getJobApplications(initialJob?.uuid),
    enabled: !!initialJob?.uuid,
  });
  const jobApplicationData = jobApplicationsQuery?.data?.data || initialJob?.job_applications || [];

  // Fetch QR code for approved jobs
  const {
    data: jobQRQuery,
    refetch: jobQRRefetch,
    isLoading: jobQRLoading,
  } = useQuery({
    queryKey: ['jobQRModal', initialJob?.uuid],
    queryFn: () => getJobQR(initialJob?.uuid),
    enabled: initialJob?.status === 3,
  });

  // Fetch cancellation list
  const {
    data: cancelQuery,
    refetch: cancelRefetch,
  } = useQuery({
    queryKey: ['cancellationListModal'],
    queryFn: getCancellationList,
    enabled: initialJob?.status === -3,
  });

  // Mutations
  const approveMutation = useMutation({
    mutationFn: initialJob?.status === -2 ? reapproveJob : approveJob,
    onSuccess: () => {
      jobApplicationsRefetch();
      setShowConfirmModal(false);
      setTimeout(() => {
        alert('Approved! You have successfully approved this applicant.');
      }, 300);
      onRefresh?.();
    },
    onError: (error) => {
      setShowConfirmModal(false);
      alert('Oops! ' + (error.response?.data?.message || 'Something went wrong'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteJob,
    onSuccess: () => {
      onClose?.();
      onRefresh?.();
    },
    onError: (error) => {
      alert('Oops! ' + (error.response?.data?.message || 'Something went wrong'));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: submitCancellation,
    onSuccess: () => {
      alert('Cancellation Submitted. You have submitted a cancellation for this job.');
      onClose?.();
      onRefresh?.();
    },
    onError: (error) => {
      alert('Oops! ' + (error.response?.data?.message || 'Something went wrong'));
    },
  });

  // Helper functions
  const getApplicantStatus = (applicant) => {
    const statusCode = applicant.status_code ?? applicant.status ?? applicant.job_application_status_code;
    if (statusCode === 3 || statusCode === 4) return 'approved';
    if (statusCode === 1 || statusCode === 2) return 'pending-applicant';
    if (statusCode === -1 || statusCode === -2) return 'declined';
    if (statusCode === -4) return 'cancelled';
    if (statusCode === 0 && isNonLocum) return 'applied';
    return 'pending';
  };

  const handleRefresh = () => {
    setRefreshing(true);
    jobApplicationsRefetch();
    jobRefetch();
    setTimeout(() => setRefreshing(false), 1000);
  };

  const handleApprove = (applicant) => {
    if (isNonLocum) return;
    setSelectedApplicant(applicant);
    setConfirmAction('approve');
    setShowConfirmModal(true);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      deleteMutation.mutate(initialJob?.uuid);
    }
  };

  const handleCancel = () => {
    const reason = window.prompt('Please enter a reason for cancellation:');
    if (reason) {
      cancelMutation.mutate({
        uuid: initialJob?.uuid,
        data: { request_reason: reason },
      });
    }
  };

  const confirmApproval = () => {
    if (selectedApplicant) {
      approveMutation.mutate({ uuid: selectedApplicant.uuid });
    }
  };

  // Filter applicants
  const approvedApplicants = jobApplicationData.filter((val) => {
    const statusCode = val.status_code ?? val.status ?? val.job_application_status_code;
    return statusCode > 0;
  });
  const pendingApplicants = jobApplicationData.filter((val) => {
    const statusCode = val.status_code ?? val.status ?? val.job_application_status_code;
    return statusCode <= 0 && statusCode !== -4;
  });

  // Currency display
  const displayCurrency = getJobDisplayCurrency(jobData, currencyPreset);
  const isFullTime = isFullTimeJob(jobData?.type);
  const isNonLocum = isFullTime;

  // Calculate hours
  const diffHours = Math.abs(
    moment(jobData?.start_timestamp).diff(moment(jobData?.end_timestamp), 'hours') || 1
  );

  const isLoading = jobLoading || jobApplicationsLoading;

  // Facilities can still edit a posting until someone applies (matches the native
  // Details screen, which gates its edit action on `jobCount < 1`).
  const allowEdit = !!onEdit && jobApplicationData.length < 1;

  // Drafts aren't publicly listed, so there is nothing to share yet
  const isDraftJob = parseInt(jobData?.type) <= -10;

  // Facilities share their posting to reach candidates, so this always points at
  // the candidate-facing site (not the employer portal the facility is browsing on).
  const shareUrl = initialJob?.uuid
    ? `https://hiresupermedic.com/job/${initialJob.uuid}`
    : undefined;

  return (
    <View style={styles.container}>
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={webColors.primary} />
        </View>
      ) : (
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
          }
        >
          {/* Job Header */}
          <View style={[styles.jobHeader, isMobile && styles.jobHeaderMobile]}>
            <View style={[styles.jobHeaderLeft, isMobile && styles.jobHeaderLeftMobile]}>
              <Text style={styles.jobTitle}>{jobData?.title}</Text>
              <Text style={styles.jobSubtitle}>{jobData?.description || 'Healthcare position'}</Text>

              {/* Job Type Tags */}
              <View style={[styles.tagsRow, isMobile && styles.tagsRowMobile]}>
                <View style={styles.jobTypeTag}>
                  <Text style={styles.jobTypeTagText}>
                    {jobTypes.find(t => t.id === parseInt(jobData?.type))?.name || 'Locum'}
                  </Text>
                </View>
                {!jobData?.is_cash && (
                  <View style={styles.spayTag}>
                    <Text style={styles.spayTagText}>S-PAY</Text>
                  </View>
                )}
              </View>
            </View>

            <View style={[styles.jobHeaderRight, isMobile && styles.jobHeaderRightMobile]}>
              <Text style={styles.jobDate}>{moment(jobData?.start_timestamp).format('Do MMM')}</Text>
              <Text style={styles.jobTime}>
                {moment(jobData?.start_timestamp).format('h:mm A')}
              </Text>
              <Text style={styles.jobTimeTo}>to</Text>
              <Text style={styles.jobTime}>
                {moment(jobData?.end_timestamp).format('h:mm A')}
              </Text>
              {!isFullTime && (
                <Text style={styles.jobPrice}>
                  {formatPrice(jobData?.price, displayCurrency)}
                </Text>
              )}
            </View>
          </View>

          {/* View Job Description Button */}
          <TouchableOpacity style={styles.viewDescBtn} onPress={onViewDetails}>
            <Text style={styles.viewDescBtnText}>View Job Description</Text>
          </TouchableOpacity>

          {/* Edit Job (while nobody has applied yet) */}
          {allowEdit && (
            <TouchableOpacity style={styles.editBtn} onPress={() => onEdit(jobData)}>
              <MaterialCommunityIcons name="pencil-outline" size={16} color={webColors.primary} />
              <Text style={styles.editBtnText}>Edit This Job</Text>
            </TouchableOpacity>
          )}

          {/* Share Job */}
          {!isDraftJob && (
            <JobShareButton
              job={jobData}
              shareUrl={shareUrl}
              buttonStyle={styles.shareBtn}
              buttonTextStyle={styles.shareBtnText}
              iconColor={webColors.primary}
            />
          )}

          {/* QR Code Button (for approved jobs) */}
          {initialJob?.status === 3 && jobQRQuery?.data?.data?.qr && (
            <TouchableOpacity style={styles.qrBtn} onPress={() => setShowQRModal(true)}>
              <MaterialCommunityIcons name="qrcode" size={20} color={webColors.primary} />
              <Text style={styles.qrBtnText}>Show QR Code</Text>
            </TouchableOpacity>
          )}

          {/* Approved Applicants Section */}
          {approvedApplicants.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>
                {initialJob?.status < 2 ? 'SELECTED' : 'APPROVED'} APPLICANTS
              </Text>
              {approvedApplicants.map((applicant, index) => (
                <ApplicantCard
                  key={applicant.uuid || index}
                  applicant={applicant}
                  status={getApplicantStatus(applicant)}
                  isApproved={true}
                  onPress={() => onViewProfile?.(applicant)}
                />
              ))}
            </View>
          )}

          {/* Reminder for approved jobs */}
          {[2, 3].includes(initialJob?.status) && (
            <View style={styles.reminderBox}>
              <View style={styles.reminderHeader}>
                <MaterialCommunityIcons name="alert" size={16} color="#f59e0b" />
                <Text style={styles.reminderTitle}>Reminder</Text>
              </View>
              <Text style={styles.reminderText}>
                {jobData?.is_cash
                  ? 'You are required to pay the total shift amount to the applicant upon completion of the shift.\n\nBy booking this shift you are agreeing to directly liaise with the applicant.'
                  : 'You will be paying the total shift fee upfront to Supermedic upon successful confirmation of the Doctor.\n\nPayments will be made directly from Supermedic to the Doctor.'}
              </Text>
            </View>
          )}

          {/* All/Pending Applicants Section */}
          {pendingApplicants.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>ALL APPLICANTS</Text>
              {pendingApplicants.map((applicant, index) => (
                <ApplicantCard
                  key={applicant.uuid || index}
                  applicant={applicant}
                  status={getApplicantStatus(applicant)}
                  isApproved={false}
                  onPress={() => onViewProfile?.(applicant)}
                />
              ))}
            </View>
          )}

          {/* No Applicants Message */}
          {jobApplicationData.length === 0 && !isLoading && (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="timer-sand-empty" size={24} color="rgba(0,0,0,0.2)" />
              <Text style={styles.emptyStateText}>No applications yet</Text>
            </View>
          )}

          {/* Delete Job (for unfilled jobs) */}
          {initialJob?.status == null && (
            <TouchableOpacity style={styles.deleteBtn} onPress={handleDelete}>
              <MaterialCommunityIcons name="delete-outline" size={16} color="#ef4444" />
              <Text style={styles.deleteBtnText}>Delete This Job</Text>
            </TouchableOpacity>
          )}

          {/* Cancel Job (for approved jobs) */}
          {[2, 3].includes(initialJob?.status) && (
            <TouchableOpacity style={styles.cancelBtn} onPress={handleCancel}>
              <MaterialCommunityIcons name="cancel" size={16} color="#ef4444" />
              <Text style={styles.cancelBtnText}>Cancel This Job</Text>
            </TouchableOpacity>
          )}

          <View style={{ height: 24 }} />
        </ScrollView>
      )}

      {/* QR Code Modal */}
      <WebModal
        visible={showQRModal}
        onClose={() => setShowQRModal(false)}
        title="QR Code"
        subtitle="Scan for check-in"
        maxWidth={400}
      >
        <View style={styles.qrModalContent}>
          {jobQRQuery?.data?.data?.qr && (
            <QRCode
              value={jobQRQuery.data.data.qr}
              size={250}
              logoBackgroundColor="transparent"
            />
          )}
        </View>
      </WebModal>

      {/* Confirmation Modal */}
      <WebModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm Action"
        maxWidth={400}
      >
        <View style={styles.confirmModalContent}>
          <Text style={styles.confirmText}>
            Are you sure you want to approve this applicant?
          </Text>
          <View style={styles.confirmButtons}>
            <TouchableOpacity
              style={styles.confirmCancelBtn}
              onPress={() => setShowConfirmModal(false)}
            >
              <Text style={styles.confirmCancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.confirmOkBtn, approveMutation.isPending && styles.btnDisabled]}
              onPress={confirmApproval}
              disabled={approveMutation.isPending}
            >
              <Text style={styles.confirmOkText}>
                {approveMutation.isPending ? 'Processing...' : 'Approve'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </WebModal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  scrollView: {
    flex: 1,
  },
  // Job Header
  jobHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
    marginBottom: 16,
  },
  jobHeaderLeft: {
    flex: 1,
    marginRight: 16,
  },
  jobHeaderRight: {
    alignItems: 'flex-end',
  },
  jobTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  jobSubtitle: {
    fontSize: 14,
    color: webColors.textMuted,
    marginBottom: 12,
  },
  tagsRow: {
    flexDirection: 'row',
    gap: 8,
  },
  jobTypeTag: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: webColors.primaryLight,
    borderRadius: 6,
  },
  jobTypeTagText: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.primary,
  },
  spayTag: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: '#fee2e2',
    borderRadius: 6,
  },
  spayTagText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FC6767',
  },
  jobDate: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  jobTime: {
    fontSize: 13,
    color: webColors.textSecondary,
  },
  jobTimeTo: {
    fontSize: 11,
    color: webColors.textMuted,
    marginVertical: 2,
  },
  jobPrice: {
    fontSize: 16,
    fontWeight: '700',
    color: webColors.primary,
    marginTop: 8,
  },
  // View Description Button
  viewDescBtn: {
    paddingVertical: 12,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: webColors.border,
  },
  viewDescBtnText: {
    fontSize: 14,
    color: webColors.textSecondary,
    fontWeight: '500',
  },
  editBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    backgroundColor: webColors.primaryLight,
    borderRadius: 8,
    marginBottom: 16,
    gap: 8,
  },
  editBtnText: {
    fontSize: 14,
    color: webColors.primary,
    fontWeight: '500',
  },
  shareBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    backgroundColor: webColors.primaryLight,
    borderRadius: 8,
    marginBottom: 16,
  },
  shareBtnText: {
    fontSize: 14,
    color: webColors.primary,
    fontWeight: '500',
  },
  // QR Button
  qrBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    backgroundColor: webColors.primaryLight,
    borderRadius: 8,
    marginBottom: 16,
    gap: 8,
  },
  qrBtnText: {
    fontSize: 14,
    color: webColors.primary,
    fontWeight: '500',
  },
  // Section
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: webColors.textMuted,
    marginBottom: 12,
    letterSpacing: 0.5,
  },
  // Applicant Card
  applicantCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#f9fafb',
    borderRadius: 10,
    marginBottom: 8,
  },
  applicantAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: webColors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  applicantAvatarText: {
    fontSize: 16,
    fontWeight: '700',
    color: webColors.primary,
  },
  applicantInfo: {
    flex: 1,
  },
  applicantLocation: {
    fontSize: 11,
    color: webColors.textMuted,
    marginBottom: 2,
  },
  applicantName: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 2,
  },
  applicantRole: {
    fontSize: 12,
    color: webColors.textSecondary,
  },
  applicantStatusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  applicantStatusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  // Reminder Box
  reminderBox: {
    backgroundColor: '#fef3c7',
    borderRadius: 10,
    padding: 14,
    marginBottom: 16,
  },
  reminderHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 6,
  },
  reminderTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#92400e',
  },
  reminderText: {
    fontSize: 13,
    color: '#92400e',
    lineHeight: 20,
  },
  // Empty State
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyStateText: {
    fontSize: 14,
    color: 'rgba(0,0,0,0.3)',
    marginTop: 12,
  },
  // Action Buttons
  deleteBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    gap: 6,
    marginTop: 20,
  },
  deleteBtnText: {
    fontSize: 13,
    color: '#ef4444',
    opacity: 0.7,
  },
  cancelBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    gap: 6,
    marginTop: 20,
  },
  cancelBtnText: {
    fontSize: 13,
    color: '#ef4444',
    opacity: 0.7,
  },
  // QR Modal
  qrModalContent: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  // Confirm Modal
  confirmModalContent: {
    padding: 8,
  },
  confirmText: {
    fontSize: 15,
    color: webColors.textPrimary,
    marginBottom: 20,
    lineHeight: 22,
  },
  confirmButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  confirmCancelBtn: {
    flex: 1,
    paddingVertical: 12,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
    alignItems: 'center',
  },
  confirmCancelText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  confirmOkBtn: {
    flex: 1,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: 8,
    alignItems: 'center',
  },
  confirmOkText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  btnDisabled: {
    opacity: 0.6,
  },
  // ==================== MOBILE VARIANTS ====================
  jobHeaderMobile: {
    flexDirection: 'column',
    gap: 12,
  },
  jobHeaderLeftMobile: {
    marginRight: 0,
  },
  jobHeaderRightMobile: {
    alignItems: 'flex-start',
  },
  tagsRowMobile: {
    flexWrap: 'wrap',
  },
});

export default JobDescriptionContent;
